/** Различные содержательные разделы посадочных страниц инструментов. */
import { UIKit } from "@/shared/ui";
import { HeroPlan } from "@/widgets/landing";

/** Какие данные можно подготовить на плане квартиры. */
export const FloorPlanDetails = () => (
	<section className="border-t border-border bg-bg text-ink">
		<div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-20">
			<div className="grid gap-6 lg:grid-cols-2 lg:gap-16">
				<div>
					<UIKit.Text as="span" className="text-xs font-semibold text-accent">
						ПЛАН КВАРТИРЫ
					</UIKit.Text>
					<h2 className="mt-3 text-3xl font-bold">Один чертёж для всего проекта</h2>
				</div>
				<UIKit.Text className="max-w-xl text-base leading-relaxed text-muted">
					Постройте контур по размерам, разделите его на помещения и разместите проёмы. Мебель,
					электрика и отделка остаются на отдельных слоях одного плана.
				</UIKit.Text>
			</div>
			<div className="mx-auto mt-10 max-w-3xl">
				<HeroPlan />
			</div>
			<dl className="mt-10 grid border-t border-border md:grid-cols-3">
				<div className="border-b border-border py-5 md:pr-8">
					<dt className="text-lg font-semibold">Стены и помещения</dt>
					<dd className="mt-2 text-sm leading-relaxed text-muted">
						Чистые размеры стен, площади комнат и наружные габариты.
					</dd>
				</div>
				<div className="border-b border-border py-5 md:px-8">
					<dt className="text-lg font-semibold">Двери и окна</dt>
					<dd className="mt-2 text-sm leading-relaxed text-muted">
						Проёмы привязаны к стенам, их размеры и положение можно изменить.
					</dd>
				</div>
				<div className="border-b border-border py-5 md:pl-8">
					<dt className="text-lg font-semibold">Слои проекта</dt>
					<dd className="mt-2 text-sm leading-relaxed text-muted">
						Скрывайте мебель или электрику, не удаляя их из чертежа.
					</dd>
				</div>
			</dl>
		</div>
	</section>
);

/** Формы кухни и независимая настройка секций конструктора. */
export const KitchenDetails = () => (
	<section className="border-t border-border bg-bg text-ink">
		<div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-20">
			<UIKit.Text as="span" className="text-xs font-semibold text-accent">
				ФОРМА И СОСТАВ
			</UIKit.Text>
			<h2 className="mt-3 max-w-3xl text-3xl font-bold sm:text-4xl">Кухня, собранная по секциям</h2>
			<div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
				{[
					["Прямая", "Один ряд вдоль стены."],
					["Угловая", "Две стороны для использования угла."],
					["П-образная", "Три стороны с рабочими поверхностями."],
					["Остров", "Дополнительная отдельно стоящая часть."],
				].map(([title, text]) => (
					<div key={title} className="border-t-2 border-accent pt-4">
						<h3 className="text-lg font-semibold">{title}</h3>
						<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">{text}</UIKit.Text>
					</div>
				))}
			</div>
			<div className="mt-12 grid gap-4 border-t border-border pt-6 md:grid-cols-2 md:gap-12">
				<h3 className="text-xl font-semibold">Верхние и нижние модули настраиваются отдельно</h3>
				<UIKit.Text className="text-sm leading-relaxed text-muted">
					Меняйте ширину секций, фасады и число ящиков, задавайте размеры и расположение техники. В
					3D можно открыть фасады и посмотреть кухню спереди или сверху.
				</UIKit.Text>
			</div>
		</div>
	</section>
);

/** Проверки расстановки мебели в одной комнате. */
export const RoomDetails = () => (
	<section className="border-t border-border bg-bg text-ink">
		<div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-8 lg:grid-cols-3 lg:py-20">
			<div>
				<UIKit.Text as="span" className="text-xs font-semibold text-accent">
					ПЕРЕД РАССТАНОВКОЙ
				</UIKit.Text>
				<h2 className="mt-3 text-3xl font-bold">Проверьте комнату в масштабе</h2>
			</div>
			<ol className="lg:col-span-2">
				{[
					[
						"Размеры в чистоте",
						"Введите внутренние размеры комнаты или начертите стены. Площадь и размерные линии появятся на плане.",
					],
					[
						"Открывание дверей",
						"Разместите дверь и окно на стене, уточните ширину проёма и направление открывания.",
					],
					[
						"Место для мебели",
						"Выберите предметы каталога, измените их размеры и проверьте расстояния до стен и других предметов.",
					],
				].map(([title, text], index) => (
					<li key={title} className="flex gap-5 border-t border-border py-5">
						<UIKit.Text as="span" className="shrink-0 text-sm font-semibold text-accent">
							0{index + 1}
						</UIKit.Text>
						<div>
							<h3 className="text-lg font-semibold">{title}</h3>
							<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">{text}</UIKit.Text>
						</div>
					</li>
				))}
			</ol>
		</div>
	</section>
);

/** Что можно увидеть и изменить в 3D-виде проекта. */
export const Interior3dDetails = () => (
	<section className="border-t border-border bg-bg text-ink">
		<div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-20">
			<div className="grid gap-6 lg:grid-cols-2 lg:gap-16">
				<h2 className="text-3xl font-bold sm:text-4xl">Тот же проект с другой стороны</h2>
				<UIKit.Text className="max-w-xl text-base leading-relaxed text-muted">
					3D-сцена строится из вашего плана: стены, проёмы и предметы не нужно создавать заново.
					Изменения в чертеже отражаются в объёмном виде.
				</UIKit.Text>
			</div>
			<div className="mt-10 grid gap-8 border-t border-border pt-6 md:grid-cols-3">
				<div>
					<h3 className="text-lg font-semibold">Мебель и сантехника</h3>
					<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">
						Размещайте предметы из каталога, меняйте их размеры и положение.
					</UIKit.Text>
				</div>
				<div>
					<h3 className="text-lg font-semibold">Поверхности</h3>
					<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">
						Подбирайте покрытия пола и отделку стен для выбранного помещения.
					</UIKit.Text>
				</div>
				<div>
					<h3 className="text-lg font-semibold">Освещение</h3>
					<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">
						Добавляйте светильники и рассматривайте интерьер с разных ракурсов.
					</UIKit.Text>
				</div>
			</div>
		</div>
	</section>
);

/** Габариты и внутреннее наполнение шкафа. */
export const WardrobeDetails = () => (
	<section className="border-t border-border bg-bg text-ink">
		<div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:py-20">
			<div>
				<UIKit.Text as="span" className="text-xs font-semibold text-accent">
					КОНСТРУКТОР ШКАФА
				</UIKit.Text>
				<h2 className="mt-3 text-3xl font-bold">От габаритов до наполнения</h2>
				<UIKit.Text className="mt-5 max-w-lg text-base leading-relaxed text-muted">
					Спроектируйте шкаф как часть помещения: задайте размер, разделите внутреннее пространство
					на секции и сохраните модель в каталог.
				</UIKit.Text>
			</div>
			<dl className="border-t border-border">
				{[
					["Габариты", "Ширина, высота и глубина под пространство комнаты."],
					["Секции", "Разделение внутреннего пространства и наполнение ячеек."],
					["Просмотр", "3D, вид спереди и сверху, размерные линии и открывание фасадов."],
					[
						"В проекте",
						"Размещение сохранённого шкафа на плане и возможность вернуться к редактированию.",
					],
				].map(([title, text]) => (
					<div key={title} className="border-b border-border py-4">
						<dt className="font-semibold">{title}</dt>
						<dd className="mt-1 text-sm leading-relaxed text-muted">{text}</dd>
					</div>
				))}
			</dl>
		</div>
	</section>
);

/** Состав развёрток и их связь с планом помещения. */
export const ElevationsDetails = () => (
	<section className="border-t border-border bg-bg text-ink">
		<div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-20">
			<UIKit.Text as="span" className="text-xs font-semibold text-accent">
				ВИДЫ СТЕН
			</UIKit.Text>
			<h2 className="mt-3 max-w-3xl text-3xl font-bold sm:text-4xl">
				Каждая стена со стороны помещения
			</h2>
			<UIKit.Text className="mt-5 max-w-2xl text-base leading-relaxed text-muted">
				Развёртки рассчитываются из плана по чистой грани комнаты. Когда меняется план, меняются и
				виды стен.
			</UIKit.Text>
			<div className="mt-10 grid gap-8 border-t border-border pt-6 md:grid-cols-3">
				<div>
					<h3 className="text-lg font-semibold">Проёмы</h3>
					<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">
						Двери и окна показываются на соответствующей стене.
					</UIKit.Text>
				</div>
				<div>
					<h3 className="text-lg font-semibold">Электрика</h3>
					<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">
						Розетки и выключатели видны со стороны помещения, где они установлены.
					</UIKit.Text>
				</div>
				<div>
					<h3 className="text-lg font-semibold">Размеры</h3>
					<UIKit.Text className="mt-2 text-sm leading-relaxed text-muted">
						Размерные цепочки помогают прочитать положение элементов и ширину стены.
					</UIKit.Text>
				</div>
			</div>
		</div>
	</section>
);
